import React, { useState } from 'react';
import { SparklesIcon, AlertCircleIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
export function HeroSection() {
  const [audioError, setAudioError] = useState(false);
  const {
    language
  } = useLanguage();
  const t = translations[language].hero;
  const scrollToBook = () => {
    const bookSection = document.getElementById('book-section');
    if (bookSection) {
      bookSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const scrollToTestimonials = () => {
    const bookSection = document.getElementById('book-section');
    if (bookSection) {
      bookSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      // Wait for scroll to complete, then scroll to testimonials within the section
      setTimeout(() => {
        const testimonialsSection = bookSection.querySelector('.testimonials-section');
        if (testimonialsSection) {
          testimonialsSection.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 500);
    }
  };
  // Use different audio based on language
  const audioUrl = language === 'es' ? 'https://share.deal.ai/files/6902f93c500a402c689c3020/d71e663b-2ff1-4919-8ed0-156f7315d3ca' : 'https://share.deal.ai/files/6900449578dfb7c892653aec/e9db3807-5ebf-415b-98aa-060af03fbee0';
  return <section className="relative min-h-screen flex items-center justify-center px-4 py-12 md:py-20 overflow-hidden" data-id="element-222">
      {/* Matrix-style background effect */}
      <div className="absolute inset-0 opacity-10" data-id="element-223">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.1)_1px,transparent_1px)] bg-[size:50px_50px]" data-id="element-224"></div>
      </div>
      <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-225">
        <div className="mb-6 md:mb-8 inline-flex items-center gap-2 px-4 py-2 bg-teal-900/30 border border-teal-500/30 rounded-full" data-id="element-226">
          <SparklesIcon className="w-4 h-4 text-yellow-400" data-id="element-227" />
          <span className="text-sm text-teal-200" data-id="element-228">{t.badge}</span>
        </div>
        <h1 className="text-4xl md:text-7xl font-bold mb-6 md:mb-8 leading-tight" data-id="element-229">
          {t.title} <span className="text-yellow-400" data-id="element-230">{t.titleHighlight}</span>
        </h1>
        <h2 className="text-xl md:text-3xl mb-6 md:mb-8 text-teal-200 font-light leading-relaxed" data-id="element-231">
          {t.subtitle}
        </h2>
        <p className="text-base md:text-xl mb-8 md:mb-12 text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-232">
          {t.description}
        </p>
        {/* Button and Audio Player Container */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 max-w-4xl mx-auto" data-id="element-233">
          <button onClick={scrollToBook} className="relative overflow-hidden w-full md:w-auto md:flex-1 max-w-md bg-gradient-to-r from-red-600 to-red-700 md:from-red-600 md:to-red-700 text-white px-8 py-5 rounded-full text-lg md:text-xl font-bold hover:from-red-500 hover:to-red-600 transform hover:scale-105 transition-all animate-pulse-glow before:content-[''] before:absolute before:inset-0 before:bg-gradient-to-r before:from-red-500 before:via-transparent before:to-red-700 before:opacity-0 md:before:opacity-100 after:content-[''] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-1/2 after:bg-red-500 after:rounded-l-full after:md:hidden" data-id="element-234">
            <span className="relative z-10" data-id="element-235">{t.cta}</span>
          </button>
          <div className="w-full md:w-auto md:flex-1 max-w-md bg-gray-900/50 p-4 rounded-lg border border-teal-500/30" data-id="element-236">
            <p className="text-sm text-teal-200 mb-3" data-id="element-237">{t.audioLabel}</p>
            {!audioError ? <audio key={audioUrl} controls controlsList="nodownload" crossOrigin="anonymous" className="w-full h-12 rounded" preload="auto" onError={e => {
            console.error('Audio failed to load:', e);
            setAudioError(true);
          }} style={{
            backgroundColor: '#1f2937',
            outline: 'none'
          }} data-id="element-238">
                <source src={audioUrl} type="audio/mpeg" data-id="element-239" />
                Your browser does not support the audio element.
              </audio> : <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-500/30 rounded text-red-300" data-id="element-240">
                <AlertCircleIcon className="w-5 h-5 flex-shrink-0" data-id="element-241" />
                <p className="text-sm" data-id="element-242">
                  Audio unavailable. The file may require special access
                  permissions.
                </p>
              </div>}
          </div>
        </div>
        {/* Testimonial CTA - Only for Spanish */}
        {language === 'es' && <div className="mt-8 max-w-2xl mx-auto" data-id="element-243">
            <div className="bg-gradient-to-br from-purple-950/30 to-black border border-purple-600/30 rounded-xl p-6" data-id="element-244">
              <p className="text-sm text-gray-400 mb-2" data-id="element-245">
                {t.testimonialCTA?.subheadline || 'No tomes mi palabra — escucha lo que dice uno de nuestros alumnos'}
              </p>
              <p className="text-base text-teal-200 italic mb-4" data-id="element-246">
                {t.testimonialCTA?.quote || 'Esto es lo que pasa cuando confías en el proceso…'}
              </p>
              <button onClick={scrollToTestimonials} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-full text-base font-bold hover:from-purple-500 hover:to-purple-600 transform hover:scale-105 transition-all" data-id="element-247">
                {t.testimonialCTA?.cta || 'Mira cómo cambió su vida'}
              </button>
            </div>
          </div>}
      </div>
      <style data-id="element-248">{`
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.5), 0 0 40px rgba(59, 130, 246, 0.3), 0 0 60px rgba(59, 130, 246, 0.2);
          }
          50% {
            box-shadow: 0 0 30px rgba(59, 130, 246, 0.8), 0 0 60px rgba(59, 130, 246, 0.5), 0 0 90px rgba(59, 130, 246, 0.3);
          }
        }
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
        audio::-webkit-media-controls-panel {
          background-color: #1f2937;
        }
        audio::-webkit-media-controls-play-button,
        audio::-webkit-media-controls-current-time-display,
        audio::-webkit-media-controls-time-remaining-display {
          color: #5eead4;
        }
      `}</style>
    </section>;
}